import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Calendar, X, ChevronLeft, ChevronRight, CheckCircle, XCircle, Clock, LogIn, LogOut } from "lucide-react";
import { FaUsers } from "react-icons/fa";
import { Mail } from "lucide-react";
import api from "../../api/axios";

// ─────────────────────────────────────────────
//  MemberAttendanceModal
// ─────────────────────────────────────────────
function MemberAttendanceModal({ member, projectId, onClose }) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  useEffect(() => {
    fetchAttendance();
  }, [currentMonth, currentYear]);

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      setSelectedRecord(null);
      const response = await api.get(`/attendance/member/${member.id}`);
      const allRecords = response.data.data || [];

      // Filter by selected month/year
      const filtered = allRecords.filter((r) => {
        const d = new Date(r.attendance_date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      });

      setRecords(filtered);
      setError(null);
    } catch (err) {
      setError("Failed to load attendance");
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const isCurrentMonth = currentMonth === today.getMonth() && currentYear === today.getFullYear();

  // Build a map: "YYYY-MM-DD" → record
  const attendanceMap = {};
  records.forEach((r) => {
    attendanceMap[r.attendance_date] = r;
  });

  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const getRecord = (day) => {
    if (!day) return null;
    const key = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return attendanceMap[key] || null;
  };

  const statusStyle = (status) => {
    if (status === "present") return "bg-green-100 text-green-700 border-green-300 font-semibold";
    if (status === "absent") return "bg-red-100 text-red-600 border-red-300 font-semibold";
    if (status === "half-day") return "bg-yellow-100 text-yellow-700 border-yellow-300 font-semibold";
    return "bg-gray-50 text-gray-500 border-gray-200";
  };

  const statusDot = (status) => {
    if (status === "present") return "bg-green-500";
    if (status === "absent") return "bg-red-500";
    if (status === "half-day") return "bg-yellow-400";
    return "";
  };

  const presentCount = records.filter(r => r.status === "present").length;
  const absentCount = records.filter(r => r.status === "absent").length;
  const halfDayCount = records.filter(r => r.status === "half-day").length;
  const totalHours = records.reduce((sum, r) => sum + parseFloat(r.total_hours || 0), 0).toFixed(2);

  const formatTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold text-white">{member.member_name}</h3>
              <p className="text-blue-200 text-sm mt-0.5">{member.designation} • {member.employee_code || "No Code"}</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors">
              <X size={20} />
            </button>
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
              <CheckCircle size={14} className="text-green-300" />
              <span className="text-white text-xs font-medium">{presentCount} Present</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
              <XCircle size={14} className="text-red-300" />
              <span className="text-white text-xs font-medium">{absentCount} Absent</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
              <Clock size={14} className="text-yellow-300" />
              <span className="text-white text-xs font-medium">{halfDayCount} Half-day</span>
            </div>
            <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
              <Clock size={14} className="text-blue-200" />
              <span className="text-white text-xs font-medium">{totalHours}h Total</span>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-6 py-4">

          {/* Month navigator */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors">
              <ChevronLeft size={18} />
            </button>
            <h4 className="text-base font-bold text-gray-800">{monthNames[currentMonth]} {currentYear}</h4>
            <button
              onClick={nextMonth}
              disabled={isCurrentMonth}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
              <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          {loading ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Loading attendance...</div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {cells.map((day, idx) => {
                const record = getRecord(day);
                const status = record?.status || null;
                const isToday = day === today.getDate() && isCurrentMonth;
                const isSelected = selectedRecord && record && selectedRecord.id === record.id;
                return (
                  <div
                    key={idx}
                    onClick={() => record && setSelectedRecord(isSelected ? null : record)}
                    className={`
                      relative aspect-square flex flex-col items-center justify-center rounded-lg border text-sm transition-all
                      ${day ? statusStyle(status) : "border-transparent"}
                      ${isToday ? "ring-2 ring-blue-500 ring-offset-1" : ""}
                      ${record ? "cursor-pointer hover:opacity-80" : ""}
                      ${isSelected ? "ring-2 ring-indigo-500 ring-offset-1" : ""}
                    `}
                  >
                    {day && (
                      <>
                        <span className={isToday ? "font-bold text-blue-600" : ""}>{day}</span>
                        {status && <span className={`mt-0.5 w-1.5 h-1.5 rounded-full ${statusDot(status)}`} />}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-gray-100 flex-wrap">
            {[
              { color: "bg-green-500", label: "Present" },
              { color: "bg-red-500", label: "Absent" },
              { color: "bg-yellow-400", label: "Half-day" },
              { color: "bg-gray-200", label: "No data" },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
                <span className="text-xs text-gray-500">{label}</span>
              </div>
            ))}
          </div>

          {/* Session detail panel — shown when a day is clicked */}
          {selectedRecord && (
            <div className="mt-4 border border-indigo-100 rounded-xl overflow-hidden">
              <div className="bg-indigo-50 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-indigo-500" />
                  <span className="text-sm font-semibold text-indigo-800">
                    {new Date(selectedRecord.attendance_date).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-indigo-500 font-medium">
                    {parseFloat(selectedRecord.total_hours).toFixed(2)}h
                  </span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize
                    ${selectedRecord.status === "present" ? "bg-green-100 text-green-700" :
                      selectedRecord.status === "absent" ? "bg-red-100 text-red-700" :
                        "bg-yellow-100 text-yellow-700"}`}>
                    {selectedRecord.status}
                  </span>
                  <button onClick={() => setSelectedRecord(null)} className="text-indigo-400 hover:text-indigo-600">
                    <X size={14} />
                  </button>
                </div>
              </div>

              {selectedRecord.sessions?.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {selectedRecord.sessions.map((session, i) => (
                    <div key={session.id} className="px-4 py-3">
                      <p className="text-xs font-semibold text-gray-400 mb-2">Session {i + 1}</p>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex items-start gap-2">
                          <LogIn size={13} className="text-green-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-[10px] text-gray-400">Check in</p>
                            <p className="text-xs font-semibold text-gray-700">{formatTime(session.check_in)}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <LogOut size={13} className="text-red-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-[10px] text-gray-400">Check out</p>
                            <p className="text-xs font-semibold text-gray-700">{formatTime(session.check_out)}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <Clock size={13} className="text-gray-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-[10px] text-gray-400">Duration</p>
                            <p className="text-xs font-semibold text-gray-700">{parseFloat(session.worked_hours).toFixed(2)}h</p>
                          </div>
                        </div>
                      </div>
                      {session.remarks && (
                        <p className="mt-2 text-xs text-gray-500 italic">"{session.remarks}"</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-4 py-3 text-xs text-gray-400">No session data available.</p>
              )}
            </div>
          )}

          {error && <p className="mt-3 text-sm text-red-600 text-center">{error}</p>}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  ProjectMembersList  (unchanged)
// ─────────────────────────────────────────────
export default function ProjectMembersList() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [members, setMembers] = useState([]);
  const [projectInfo, setProjectInfo] = useState(
    location.state?.project
      ? {
        name: location.state.project.project_name,
        type: location.state.project.project_type,
        status: location.state.project.status,
      }
      : null
  );
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [attendanceMember, setAttendanceMember] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    member_name: "",
    mobile: "",
    email: "",
    designation: "",
    employee_code: "",
    can_login: false,
    email_notification: true,
  });

  useEffect(() => { fetchMembers(); }, [id]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const membersRes = await api.get(`/api/project-team/${id}`);
      setMembers(membersRes.data.data || []);
      if (!projectInfo) {
        const projectRes = await api.get(`/api/projects/${id}`);
        const p = projectRes.data.data;
        setProjectInfo({ name: p.project_name, type: p.project_type, status: p.status });
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post("/api/project-team/add", { project_id: parseInt(id), ...formData });
      setMembers([...members, response.data.data]);
      setShowAddModal(false);
      setFormData({ member_name: "", mobile: "", email: "", designation: "", employee_code: "", can_login: false, email_notification: true });
      setError(null);
    } catch (err) {
      setError("Failed to add member");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (window.confirm("Are you sure you want to remove this member?")) {
      try {
        await api.delete(`/api/project-team/remove/${memberId}`);
        setMembers(members.filter(m => m.id !== memberId));
        setError(null);
      } catch (err) {
        setError("Failed to remove member");
      }
    }
  };

  const handleNotificationToggle = async (memberId, type, currentValue) => {
    try {
      const member = members.find(m => m.id === memberId);
      const updateData = { email_notification: type === "email" ? !currentValue : member.email_notification };
      await api.post(`/api/project-team/notification/${memberId}`, updateData);
      setMembers(members.map(m => m.id === memberId ? { ...m, ...updateData } : m));
      setError(null);
    } catch (err) {
      setError("Failed to update notification");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const projectSubtitle = projectInfo
    ? `Manage team for ${projectInfo.name} • ${projectInfo.type || "Project"} • ${projectInfo.status || "Active"}`
    : `Manage team for Project #${id}`;

  const statCards = [
    { icon: <FaUsers size={18} />, label: "Total Members", value: members.length, bg: "bg-blue-600", iconBg: "bg-blue-500", text: "text-white" },
    { icon: <Mail size={18} />, label: "Email Notif ON", value: members.filter(m => m.email_notification).length, bg: "bg-emerald-500", iconBg: "bg-emerald-400", text: "text-white" },
    { icon: <Calendar size={18} />, label: "Active Today", value: "—", bg: "bg-amber-500", iconBg: "bg-amber-400", text: "text-white" },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-200 rounded-full"><ArrowLeft size={20} /></button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Project Members</h2>
            <p className="text-sm text-gray-500">{projectSubtitle}</p>
          </div>
        </div>
        <div className="flex gap-3">
          {/* <button onClick={() => navigate(`/projects/${id}/attendance`)} className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 font-semibold text-sm">
            <Calendar size={16} />Attendance
          </button> */}
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-semibold text-sm">
            <Plus size={16} />Add Member
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        {statCards.map(({ icon, label, value, bg, iconBg, text }) => (
          <div key={label} className={`${bg} rounded-xl p-4 shadow-sm`}>
            <div className={`${iconBg} w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${text} opacity-90`}>{icon}</div>
            <p className={`text-xs font-medium ${text} opacity-80 mb-0.5`}>{label}</p>
            <p className={`text-2xl font-bold ${text}`}>{value}</p>
          </div>
        ))}
      </div>

      {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-base font-bold text-gray-800">Team Members</h3>
          <button className="px-3 py-1.5 text-xs text-blue-600 font-medium hover:bg-blue-50 rounded-lg border border-blue-200">Export</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500 text-sm">Loading members...</div>
        ) : members.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm">No members found. Add your first member!</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Member", "Designation", "Emp Code", "Notifications", "Attendance", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {members.map((member, index) => (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-xs font-medium text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-800">{member.member_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{member.email}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{member.designation}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{member.employee_code || "—"}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleNotificationToggle(member.id, "email", member.email_notification)}
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${member.email_notification ? "bg-blue-100 text-blue-700 hover:bg-blue-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                      >
                        Email
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setAttendanceMember(member)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-all border border-indigo-200 text-xs font-semibold shadow-sm"
                      >
                        <Calendar size={12} />View
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-all border border-red-200 text-xs font-semibold shadow-sm"
                      >
                        <Trash2 size={12} />Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {attendanceMember && (
        <MemberAttendanceModal
          member={attendanceMember}
          projectId={id}
          onClose={() => setAttendanceMember(null)}
        />
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-lg">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Add Project Member</h3>
            <form onSubmit={handleAddMember} className="space-y-4">
              {[
                { label: "Member Name *", name: "member_name", type: "text", placeholder: "John Doe", required: true },
                { label: "Email *", name: "email", type: "email", placeholder: "john@example.com", required: true },
                { label: "Mobile *", name: "mobile", type: "tel", placeholder: "9876543210", required: true },
                { label: "Designation *", name: "designation", type: "text", placeholder: "Supervisor", required: true },
                { label: "Employee Code", name: "employee_code", type: "text", placeholder: "EMP001", required: false },
              ].map(({ label, name, type, placeholder, required }) => (
                <div key={name}>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
                  <input type={type} name={name} placeholder={placeholder} value={formData[name]} onChange={handleInputChange} required={required} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              ))}
              <div className="pt-2">
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input type="checkbox" name="email_notification" checked={formData.email_notification} onChange={handleInputChange} className="w-4 h-4 rounded" />
                  Email Notification
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-semibold text-sm">Add Member</button>
                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 font-semibold text-sm">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}